import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigationGuard } from '../../../context/NavigationGuardContext';

/**
 * Guards the Offer create/edit form against accidental navigation away while
 * there are unsaved changes. Shows an in-app confirmation dialog for:
 *
 * 1. Cancel button   — call `handleCancel()` from the button onClick.
 * 2. Browser back     — intercepted via `popstate` (a duplicate history entry is
 *                       pushed so the first back press is catchable).
 * 3. Sidebar / nav    — registers dirty state + a modal callback with the shared
 *                       `NavigationGuardContext` (DesktopLayout routes nav clicks
 *                       through `guardedNavigate`).
 *
 * Browser tab close / toolbar refresh still falls back to the native
 * `beforeunload` prompt — browsers block custom modals there by design.
 *
 * @param isDirty  Whether the form has unsaved changes.
 * @param backPath Where to go once the user confirms discarding (e.g. '/offers').
 */
export function useOfferDiscardGuard(isDirty: boolean, backPath: string) {
  const navigate = useNavigate();
  const { register, unregister, registerShowModal, unregisterShowModal } = useNavigationGuard();
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Path the sidebar/nav was trying to reach (falls back to backPath for cancel/back).
  const pendingPath = useRef<string | null>(null);
  const isDirtyRef = useRef(isDirty);

  // Keep dirty state current for the context (sidebar) and the popstate handler.
  useEffect(() => {
    isDirtyRef.current = isDirty;
    register(isDirty);
  }, [isDirty, register]);

  // Sidebar guard: when guarded nav fires while dirty, open our own modal.
  useEffect(() => {
    registerShowModal((path: string) => {
      pendingPath.current = path;
      setShowDiscardModal(true);
    });
    return () => {
      unregisterShowModal();
      unregister();
    };
  }, [registerShowModal, unregisterShowModal, unregister]);

  // Browser tab close / toolbar refresh — native prompt is the only option here.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Browser back button — intercept via popstate while dirty.
  useEffect(() => {
    if (!isDirty) return;

    // Push a duplicate entry so the first back press lands here and is catchable.
    window.history.pushState(null, '', window.location.href);

    const handler = () => {
      // Re-push to stay on the form, then surface the confirmation.
      window.history.pushState(null, '', window.location.href);
      pendingPath.current = backPath;
      setShowDiscardModal(true);
    };

    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isDirty, backPath]);

  const handleCancel = useCallback(() => {
    if (isDirtyRef.current) {
      pendingPath.current = backPath;
      setShowDiscardModal(true);
    } else {
      navigate(backPath);
    }
  }, [backPath, navigate]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardModal(false);
    isDirtyRef.current = false;
    register(false);
    const target = pendingPath.current ?? backPath;
    pendingPath.current = null;
    navigate(target);
  }, [backPath, navigate, register]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardModal(false);
    pendingPath.current = null;
  }, []);

  return {
    showDiscardModal,
    handleCancel,
    handleConfirmDiscard,
    handleKeepEditing,
  };
}
